package com.cs.ejobin.app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.cs.ejobin.data.supabase.SupabaseAuthRepository
import com.cs.ejobin.data.supabase.SupabaseAvatarRepository
import com.cs.ejobin.data.supabase.SupabaseClientProvider
import com.cs.ejobin.data.supabase.SupabaseExtrasRepository
import com.cs.ejobin.data.supabase.SupabaseProfileRepository
import com.cs.ejobin.data.supabase.SupabaseSubscriptionRepository
import com.cs.ejobin.i18n.AppLanguage
import com.cs.ejobin.i18n.I18nState
import com.cs.ejobin.i18n.ProvideI18n
import com.cs.ejobin.navigation.NavController
import com.cs.ejobin.navigation.Screen
import com.cs.ejobin.navigation.Screen.CheckoutCancel
import com.cs.ejobin.navigation.Screen.CheckoutSuccess
import com.cs.ejobin.navigation.Screen.ExtraDetail
import com.cs.ejobin.navigation.Screen.ForgotPasswordEmail
import com.cs.ejobin.navigation.Screen.ForgotPasswordOtp
import com.cs.ejobin.navigation.Screen.Home
import com.cs.ejobin.navigation.Screen.Login
import com.cs.ejobin.navigation.Screen.Paywall
import com.cs.ejobin.navigation.Screen.Profile
import com.cs.ejobin.navigation.Screen.ResetPassword
import com.cs.ejobin.navigation.Screen.Signup
import com.cs.ejobin.platform.passwordResetRedirectUrl
import com.cs.ejobin.platform.replaceUrl
import com.cs.ejobin.ui.screens.auth.ForgotPasswordOtpScreen
import com.cs.ejobin.ui.screens.auth.ForgotPasswordScreen
import com.cs.ejobin.ui.screens.auth.LoginScreen
import com.cs.ejobin.ui.screens.auth.ResetPasswordScreen
import com.cs.ejobin.ui.screens.auth.SignupScreen
import com.cs.ejobin.ui.screens.checkout.CheckoutCancelScreen
import com.cs.ejobin.ui.screens.checkout.CheckoutSuccessScreen
import com.cs.ejobin.ui.screens.extra.ExtraDetailScreen
import com.cs.ejobin.ui.screens.home.HomeScreen
import com.cs.ejobin.ui.screens.paywall.PaywallScreen
import com.cs.ejobin.ui.screens.profile.ProfileScreen
import com.cs.ejobin.vm.ExtraDetailViewModel
import com.cs.ejobin.vm.ForgotPasswordViewModel
import com.cs.ejobin.vm.HomeViewModel
import com.cs.ejobin.vm.LoginViewModel
import com.cs.ejobin.vm.PaywallViewModel
import com.cs.ejobin.vm.ProfileViewModel
import com.cs.ejobin.vm.SignupViewModel
import com.cs.ejobin.web.WebUrlSync
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.auth.status.SessionSource
import io.github.jan.supabase.auth.status.SessionStatus
import kotlinx.coroutines.flow.collectLatest

@Composable
fun AppRoot(startScreen: Screen = Login) {
    val i18n = remember { I18nState(AppLanguage.FR) }
    val session = remember { AppSession() }
    val supabase = remember {
        SupabaseClientProvider.create(
            url = "https://tgwsbarskmcddxdwqtzt.supabase.co",
            anonKey = "sb_publishable_GWDg-rHwOTCGZW2w6-EVbg_lz3QlqHL"
        )
    }
    val auth = remember { SupabaseAuthRepository(supabase, redirectTo = passwordResetRedirectUrl()) }
    val extras = remember { SupabaseExtrasRepository(supabase) }

    // On garde le repo subs pour ne rien casser côté VM,
    // mais on n'utilise plus le Paywall / gating pour l’instant.
    val subs = remember { SupabaseSubscriptionRepository(supabase) }

    val profileRepo = remember { SupabaseProfileRepository(supabase) }
    val nav = remember { NavController(startScreen) }
    val current by nav.current.collectAsState()
    WebUrlSync(nav)

    val isCheckoutEntry = startScreen == CheckoutSuccess || startScreen == CheckoutCancel
    var authReady by remember { mutableStateOf(false) }
    var isAuthed by remember { mutableStateOf(false) }

    if (!isCheckoutEntry) {
        LaunchedEffect(supabase) {
            supabase.auth.sessionStatus.collectLatest { status ->
                when (status) {
                    is SessionStatus.Authenticated -> {
                        authReady = true
                        isAuthed = true
                        if (status.source == SessionSource.External) {
                            nav.resetTo(ResetPassword)
                        }
                    }
                    is SessionStatus.NotAuthenticated -> {
                        authReady = true
                        isAuthed = false
                        if (status.isSignOut) {
                            session.logout()
                            nav.resetTo(Login)
                        }
                    }
                    SessionStatus.Initializing -> Unit
                    is SessionStatus.RefreshFailure -> {
                        authReady = true
                        isAuthed = false
                        session.logout()
                        nav.resetTo(Login)
                    }
                }
            }
        }
    }

    LaunchedEffect(authReady, isAuthed, current) {
        if (!authReady) return@LaunchedEffect
        if (!isAuthed && !isPublicScreen(current)) {
            nav.resetTo(Login)
        }
    }

    ProvideI18n(i18nState = i18n) {
        when (val s = current) {
            Login -> {
                val vm = remember { LoginViewModel(auth, session) }
                LoginScreen(
                    vm = vm,
                    onGoSignup = { nav.navigate(Signup) },
                    onGoForgot = { nav.navigate(ForgotPasswordEmail) },
                    onLoggedIn = { nav.resetTo(Home) }
                )
            }

            Signup -> {
                val vm = remember { SignupViewModel(auth, session) }
                SignupScreen(
                    vm = vm,
                    onBackToLogin = { nav.pop() },

                    // ✅ Paiement désactivé : on va direct au Home.
                    onSignedUpGoHome = { nav.resetTo(Home) },

                    onNeedEmailConfirmation = { }
                )
            }

            ForgotPasswordEmail -> {
                val vm = remember { ForgotPasswordViewModel(auth) }
                ForgotPasswordScreen(
                    vm = vm,
                    onEmailSent = { nav.navigate(ForgotPasswordOtp) },
                    onBack = { nav.pop() }
                )
            }

            ForgotPasswordOtp -> {
                ForgotPasswordOtpScreen(
                    onContinue = { nav.resetTo(Login) },
                    onBack = { nav.pop() },
                )
            }

            ResetPassword -> {
                val vm = remember { ForgotPasswordViewModel(auth) }
                ResetPasswordScreen(
                    vm = vm,
                    onDone = {
                        replaceUrl("/login")
                        nav.resetTo(Login)
                    },
                    onBack = { nav.pop() }
                )
            }

            Home -> {
                val vm = remember { HomeViewModel(extras, session) }
                HomeScreen(
                    vm = vm,
                    onOpenExtra = { id -> nav.navigate(ExtraDetail(id)) },
                    onOpenProfile = { nav.navigate(Profile) },

                    // ✅ plus de paywall
                    onNeedPaywall = { },

                    onLogout = { session.logout(); nav.resetTo(Login) }
                )
            }

            is ExtraDetail -> {
                val vm = remember { ExtraDetailViewModel(extras, session) }
                ExtraDetailScreen(
                    vm = vm,
                    extraId = s.extraId,

                    // ✅ plus de paywall
                    onNeedPaywall = { },

                    onHome = { nav.resetTo(Home) },
                    onOpenProfile = { nav.resetTo(Profile) },
                    onLogout = {
                        session.logout()
                        nav.resetTo(Login)
                    }
                )
            }

            Profile -> {
                val avatarRepo = remember { SupabaseAvatarRepository(supabase) }
                val vm = remember { ProfileViewModel(auth, profileRepo, avatarRepo, subs, session) }
                ProfileScreen(
                    vm = vm,

                    // ✅ plus de paywall
                    onOpenPaywall = { },

                    onLoggedOut = { nav.resetTo(Login) },
                    onTopLogout = {
                        session.logout()
                        nav.resetTo(Login)
                    },
                    onTopHome = { nav.resetTo(Home) },
                )
            }

            // On laisse la route existante mais tu ne l’atteins plus depuis le flow normal.
            is Paywall -> {
                val vm = remember { PaywallViewModel(subs, session) }
                PaywallScreen(
                    vm = vm,
                    onSuccess = { nav.resetTo(Home) },
                    onSkip = { nav.resetTo(Home) }
                )
            }

            CheckoutSuccess -> {
                CheckoutSuccessScreen(
                    onContinue = {
                        replaceUrl("/")
                        nav.resetTo(Home)
                    },
                    onGoProfile = {
                        replaceUrl("/profile")
                        nav.resetTo(Profile)
                    }
                )
            }

            CheckoutCancel -> {
                CheckoutCancelScreen(
                    onBackHome = {
                        replaceUrl("/")
                        nav.resetTo(Home)
                    },
                    onGoProfile = {
                        replaceUrl("/profile")
                        nav.resetTo(Profile)
                    }
                )
            }
        }
    }
}

private fun isPublicScreen(s: Screen): Boolean = when (s) {
    Login, Signup, ForgotPasswordEmail, ForgotPasswordOtp, ResetPassword,
    CheckoutSuccess, CheckoutCancel -> true
    else -> false
}